import React, { useMemo } from 'react';

/**
 * 아이소메트릭 SVG 시스템.
 *
 * WebGL 없이도 사이트 전체에 같은 시각 언어를 깔기 위한 2D 아이소 렌더러.
 * 카드 아이콘 · 빈 상태 · 인증 페이지 일러스트가 모두 이 파일에서 나온다.
 *
 * 투영 (직교 · 30°)
 *   sx = (x − y) · cos30°
 *   sy = (x + y) · sin30° − z
 *   X 는 오른쪽 아래로, Y 는 왼쪽 아래로, Z 는 위로.
 *
 * 면 색은 CSS 변수 3벌(top / left / right)로 — 토큰이 바뀌면 함께 바뀐다.
 */
const C30 = Math.cos(Math.PI / 6);
const S30 = Math.sin(Math.PI / 6);

export const project = (x, y, z = 0) => [(x - y) * C30, (x + y) * S30 - z];

const pts = list => list.map(([x, y, z]) => project(x, y, z).map(v => v.toFixed(2)).join(',')).join(' ');

const MAT = {
  steel: 'steel',
  iron: 'iron',
  blue: 'blue',
  dark: 'dark',
  paper: 'paper',
  spark: 'spark'
};

/** 박스 하나 — 보이는 3면 폴리곤 */
function boxFaces({ x, y, z = 0, w, d, h, m = 'steel', op = 1 }) {
  const top = pts([[x, y, z + h], [x + w, y, z + h], [x + w, y + d, z + h], [x, y + d, z + h]]);
  const left = pts([[x, y + d, z], [x + w, y + d, z], [x + w, y + d, z + h], [x, y + d, z + h]]);
  const right = pts([[x + w, y, z], [x + w, y + d, z], [x + w, y + d, z + h], [x + w, y, z + h]]);
  return { top, left, right, m: MAT[m] || 'steel', op };
}

/** 프리미티브: 박스 */
export const B = props => ({ kind: 'box', ...props });
/** 프리미티브: 바닥 그림자(타원) */
export const SH = props => ({ kind: 'shadow', ...props });
/** 프리미티브: 평면 위 선분 (z 높이의 (x1,y1)→(x2,y2)) */
export const L = props => ({ kind: 'line', ...props });
/** 프리미티브: 평면 위 원(디스크) — 구멍이나 스파크 */
export const D = props => ({ kind: 'disc', ...props });
/** 프리미티브: 수직 기둥형 빔 (레이저) */
export const BEAM = props => ({ kind: 'beam', ...props });

/* ── 장면 정의 ─────────────────────────────────────────────────────
   좌표 단위는 임의. 뷰박스는 자동 계산된다.                       */
const SCENES = {
  /* 판재 한 장 */
  plate: () => [
    SH({ x: -2, y: -2, w: 64, d: 44, z: 0 }),
    B({ x: 0, y: 0, w: 60, d: 40, h: 5, m: 'steel' })
  ],

  /* 시트 여러 장 적층 */
  sheets: () => [
    SH({ x: -2, y: -2, w: 66, d: 46 }),
    B({ x: 0, y: 0, w: 60, d: 40, h: 4, m: 'steel' }),
    B({ x: 4, y: 3, z: 4, w: 60, d: 40, h: 4, m: 'steel' }),
    B({ x: 8, y: 6, z: 8, w: 60, d: 40, h: 4, m: 'blue' })
  ],

  /* 레이저 헤드가 판을 자르는 중 */
  laser: () => [
    SH({ x: -2, y: -2, w: 66, d: 46 }),
    B({ x: 0, y: 0, w: 62, d: 42, h: 4, m: 'steel' }),
    L({ x1: 12, y1: 10, x2: 40, y2: 10, z: 4, m: 'kerf' }),
    L({ x1: 40, y1: 10, x2: 40, y2: 30, z: 4, m: 'kerf' }),
    L({ x1: 12, y1: 10, x2: 12, y2: 30, z: 4, m: 'slot' }),
    L({ x1: 12, y1: 30, x2: 40, y2: 30, z: 4, m: 'slot' }),
    B({ x: 36, y: 6, z: 20, w: 8, d: 8, h: 10, m: 'dark' }),
    B({ x: 38, y: 8, z: 12, w: 4, d: 4, h: 8, m: 'iron' }),
    BEAM({ x: 40, y: 10, z0: 4, z1: 12, r: 1.6 }),
    D({ x: 40, y: 10, z: 4.2, r: 4, m: 'glow' })
  ],

  /* 네스팅 — 시트 위 부품 배치 */
  nest: () => [
    SH({ x: -2, y: -2, w: 66, d: 46 }),
    B({ x: 0, y: 0, w: 62, d: 42, h: 3, m: 'steel' }),
    B({ x: 4, y: 4, z: 3, w: 24, d: 16, h: 2.5, m: 'blue' }),
    B({ x: 32, y: 4, z: 3, w: 26, d: 16, h: 2.5, m: 'blue' }),
    B({ x: 4, y: 24, z: 3, w: 14, d: 14, h: 2.5, m: 'blue' }),
    B({ x: 22, y: 24, z: 3, w: 36, d: 14, h: 2.5, m: 'blue' }),
    D({ x: 16, y: 12, z: 5.6, r: 3.5, m: 'hole' }),
    D({ x: 45, y: 12, z: 5.6, r: 3.5, m: 'hole' })
  ],

  /* 트럭 */
  truck: () => [
    SH({ x: -4, y: -2, w: 76, d: 34 }),
    B({ x: 0, y: 0, z: 6, w: 44, d: 26, h: 22, m: 'paper' }),
    B({ x: 46, y: 2, z: 6, w: 18, d: 22, h: 14, m: 'blue' }),
    B({ x: 47, y: 3, z: 12, w: 4, d: 20, h: 7, m: 'dark' }),
    B({ x: -2, y: 4, z: 4, w: 68, d: 18, h: 3, m: 'dark' }),
    B({ x: 6, y: 24, z: 0, w: 8, d: 4, h: 8, m: 'dark' }),
    B({ x: 30, y: 24, z: 0, w: 8, d: 4, h: 8, m: 'dark' }),
    B({ x: 52, y: 24, z: 0, w: 8, d: 4, h: 8, m: 'dark' })
  ],

  /* 서류 */
  doc: () => [
    SH({ x: -2, y: -2, w: 48, d: 60 }),
    B({ x: 0, y: 0, w: 42, d: 56, h: 2, m: 'paper' }),
    L({ x1: 6, y1: 10, x2: 30, y2: 10, z: 2.2, m: 'ink' }),
    L({ x1: 6, y1: 18, x2: 36, y2: 18, z: 2.2, m: 'ink' }),
    L({ x1: 6, y1: 26, x2: 36, y2: 26, z: 2.2, m: 'ink' }),
    L({ x1: 6, y1: 34, x2: 24, y2: 34, z: 2.2, m: 'ink' })
  ],

  /* 증명서 — 서류 + 인장 */
  cert: () => [
    SH({ x: -2, y: -2, w: 48, d: 60 }),
    B({ x: 0, y: 0, w: 42, d: 56, h: 2, m: 'paper' }),
    L({ x1: 6, y1: 10, x2: 36, y2: 10, z: 2.2, m: 'ink' }),
    L({ x1: 6, y1: 18, x2: 36, y2: 18, z: 2.2, m: 'ink' }),
    L({ x1: 6, y1: 26, x2: 26, y2: 26, z: 2.2, m: 'ink' }),
    D({ x: 30, y: 44, z: 2.4, r: 7, m: 'seal' }),
    D({ x: 30, y: 44, z: 2.6, r: 4, m: 'sealIn' })
  ],

  /* 팔레트 위 적재 */
  stack: () => [
    SH({ x: -2, y: -2, w: 60, d: 50 }),
    B({ x: 0, y: 0, w: 56, d: 46, h: 5, m: 'dark' }),
    B({ x: 4, y: 4, z: 5, w: 48, d: 38, h: 4, m: 'steel' }),
    B({ x: 5, y: 5, z: 9, w: 46, d: 36, h: 4, m: 'steel' }),
    B({ x: 6, y: 6, z: 13, w: 44, d: 34, h: 4, m: 'steel' }),
    B({ x: 7, y: 7, z: 17, w: 42, d: 32, h: 4, m: 'blue' })
  ],

  /* 업로드 — 판 + 화살표 */
  upload: () => [
    SH({ x: -2, y: -2, w: 64, d: 44 }),
    B({ x: 0, y: 0, w: 60, d: 40, h: 4, m: 'steel' }),
    B({ x: 24, y: 14, z: 4, w: 12, d: 12, h: 14, m: 'blue' }),
    B({ x: 18, y: 8, z: 18, w: 24, d: 24, h: 3, m: 'blue' })
  ],

  /* 절단 셀 (소형) */
  cell: () => [
    SH({ x: -4, y: -4, w: 76, d: 56 }),
    B({ x: 0, y: 0, w: 70, d: 50, h: 14, m: 'paper' }),
    B({ x: 4, y: 4, z: 14, w: 62, d: 42, h: 2, m: 'dark' }),
    B({ x: 8, y: 8, z: 16, w: 54, d: 34, h: 2, m: 'blue' }),
    B({ x: 30, y: -2, z: 16, w: 6, d: 54, h: 6, m: 'steel' }),
    B({ x: 30, y: 20, z: 22, w: 6, d: 8, h: 6, m: 'dark' }),
    BEAM({ x: 33, y: 24, z0: 18, z1: 22, r: 1.4 }),
    D({ x: 33, y: 24, z: 18.2, r: 3, m: 'glow' })
  ],

  /* 견적 데스크 — 모니터 + 도면 */
  desk: () => [
    SH({ x: -4, y: -4, w: 72, d: 52 }),
    B({ x: 0, y: 0, w: 66, d: 44, h: 4, m: 'paper' }),
    B({ x: 36, y: 6, z: 4, w: 4, d: 26, h: 20, m: 'dark' }),
    B({ x: 35, y: 8, z: 7, w: 1.5, d: 22, h: 15, m: 'screen' }),
    B({ x: 6, y: 10, z: 4, w: 24, d: 28, h: 1.5, m: 'paper' }),
    L({ x1: 10, y1: 14, x2: 26, y2: 14, z: 5.7, m: 'blue' }),
    L({ x1: 10, y1: 14, x2: 10, y2: 34, z: 5.7, m: 'blue' }),
    L({ x1: 26, y1: 14, x2: 26, y2: 34, z: 5.7, m: 'blue' }),
    L({ x1: 10, y1: 34, x2: 26, y2: 34, z: 5.7, m: 'blue' }),
    D({ x: 18, y: 24, z: 5.8, r: 4, m: 'holeBlue' })
  ],

  /* 계산기/견적서 */
  calc: () => [
    SH({ x: -2, y: -2, w: 48, d: 60 }),
    B({ x: 0, y: 0, w: 42, d: 56, h: 3, m: 'dark' }),
    B({ x: 4, y: 4, z: 3, w: 34, d: 14, h: 1.5, m: 'screen' }),
    ...[0, 1, 2].flatMap(r => [0, 1, 2].map(c => B({ x: 4 + c * 12, y: 22 + r * 11, z: 3, w: 10, d: 9, h: 2, m: c === 2 && r === 2 ? 'blue' : 'paper' })))
  ],

  /* 사용자 배지 */
  user: () => [
    SH({ x: -2, y: -2, w: 50, d: 50 }),
    B({ x: 0, y: 0, w: 46, d: 46, h: 3, m: 'paper' }),
    D({ x: 23, y: 16, z: 3.2, r: 8, m: 'blueDisc' }),
    B({ x: 9, y: 26, z: 3, w: 28, d: 14, h: 6, m: 'blue' })
  ],

  /* 빈 상태 — 빈 팔레트 */
  empty: () => [
    SH({ x: -2, y: -2, w: 64, d: 50 }),
    B({ x: 0, y: 0, w: 60, d: 46, h: 4, m: 'iron' }),
    B({ x: 0, y: 0, z: 4, w: 60, d: 6, h: 3, m: 'steel' }),
    B({ x: 0, y: 20, z: 4, w: 60, d: 6, h: 3, m: 'steel' }),
    B({ x: 0, y: 40, z: 4, w: 60, d: 6, h: 3, m: 'steel' })
  ],

  /* 검색 */
  search: () => [
    SH({ x: -2, y: -2, w: 64, d: 46 }),
    B({ x: 0, y: 0, w: 60, d: 42, h: 3, m: 'paper' }),
    D({ x: 26, y: 20, z: 3.4, r: 12, m: 'ring' }),
    D({ x: 26, y: 20, z: 3.6, r: 8, m: 'hole' }),
    B({ x: 34, y: 28, z: 3, w: 16, d: 5, h: 4, m: 'dark' })
  ],

  /* 알림 — 종 대신 우편함 느낌의 박스 */
  inbox: () => [
    SH({ x: -2, y: -2, w: 64, d: 48 }),
    B({ x: 0, y: 0, w: 60, d: 44, h: 16, m: 'paper' }),
    B({ x: 4, y: 4, z: 16, w: 52, d: 36, h: 1, m: 'dark' }),
    B({ x: 10, y: 8, z: 17, w: 40, d: 28, h: 3, m: 'blue' })
  ],

  /* 발송 완료 — 소포 + 체크 */
  sent: () => [
    SH({ x: -4, y: -4, w: 64, d: 56 }),
    B({ x: 0, y: 0, w: 46, d: 46, h: 30, m: 'paper' }),
    B({ x: 21, y: -1, z: 0, w: 4, d: 48, h: 31, m: 'blue' }),
    B({ x: -1, y: 21, z: 0, w: 48, d: 4, h: 31, m: 'blue' }),
    D({ x: 23, y: 23, z: 30.5, r: 9, m: 'okDisc' })
  ],

  /* 로그인 — 큰 데스크 장면 */
  login: () => [
    SH({ x: -10, y: -8, w: 140, d: 100 }),
    B({ x: 0, y: 0, w: 120, d: 80, h: 6, m: 'paper' }),
    B({ x: 74, y: 10, z: 6, w: 6, d: 46, h: 38, m: 'dark' }),
    B({ x: 72.5, y: 13, z: 12, w: 2, d: 40, h: 28, m: 'screen' }),
    B({ x: 78, y: 26, z: 0, w: 6, d: 14, h: 6, m: 'dark' }),
    B({ x: 10, y: 14, z: 6, w: 48, d: 56, h: 2, m: 'paper' }),
    L({ x1: 16, y1: 20, x2: 52, y2: 20, z: 8.3, m: 'blue' }),
    L({ x1: 16, y1: 20, x2: 16, y2: 64, z: 8.3, m: 'blue' }),
    L({ x1: 52, y1: 20, x2: 52, y2: 64, z: 8.3, m: 'blue' }),
    L({ x1: 16, y1: 64, x2: 52, y2: 64, z: 8.3, m: 'blue' }),
    D({ x: 34, y: 42, z: 8.4, r: 10, m: 'holeBlue' }),
    D({ x: 22, y: 26, z: 8.4, r: 2.5, m: 'holeBlue' }),
    D({ x: 46, y: 26, z: 8.4, r: 2.5, m: 'holeBlue' }),
    D({ x: 22, y: 58, z: 8.4, r: 2.5, m: 'holeBlue' }),
    D({ x: 46, y: 58, z: 8.4, r: 2.5, m: 'holeBlue' }),
    B({ x: 96, y: 60, z: 6, w: 18, d: 14, h: 10, m: 'blue' }),
    B({ x: 98, y: 62, z: 16, w: 14, d: 10, h: 2, m: 'steel' })
  ],

  /* 회원가입 — 적재 + 증명서 */
  signup: () => [
    SH({ x: -10, y: -8, w: 140, d: 100 }),
    B({ x: 0, y: 10, w: 70, d: 60, h: 6, m: 'dark' }),
    B({ x: 5, y: 15, z: 6, w: 60, d: 50, h: 5, m: 'steel' }),
    B({ x: 7, y: 17, z: 11, w: 56, d: 46, h: 5, m: 'steel' }),
    B({ x: 9, y: 19, z: 16, w: 52, d: 42, h: 5, m: 'steel' }),
    B({ x: 11, y: 21, z: 21, w: 48, d: 38, h: 5, m: 'blue' }),
    B({ x: 84, y: 24, z: 0, w: 40, d: 54, h: 2, m: 'paper' }),
    L({ x1: 90, y1: 32, x2: 116, y2: 32, z: 2.3, m: 'ink' }),
    L({ x1: 90, y1: 40, x2: 118, y2: 40, z: 2.3, m: 'ink' }),
    L({ x1: 90, y1: 48, x2: 108, y2: 48, z: 2.3, m: 'ink' }),
    D({ x: 110, y: 66, z: 2.5, r: 7, m: 'seal' }),
    D({ x: 110, y: 66, z: 2.7, r: 4, m: 'sealIn' })
  ],

  /* 견적 요청 페이지 히어로 */
  quote: () => [
    SH({ x: -10, y: -8, w: 150, d: 100 }),
    B({ x: 0, y: 0, w: 130, d: 84, h: 5, m: 'steel' }),
    B({ x: 8, y: 8, z: 5, w: 44, d: 30, h: 3, m: 'blue' }),
    B({ x: 58, y: 8, z: 5, w: 60, d: 30, h: 3, m: 'blue' }),
    B({ x: 8, y: 44, z: 5, w: 30, d: 30, h: 3, m: 'blue' }),
    B({ x: 44, y: 44, z: 5, w: 74, d: 30, h: 3, m: 'blue' }),
    D({ x: 30, y: 23, z: 8.3, r: 7, m: 'hole' }),
    D({ x: 88, y: 23, z: 8.3, r: 9, m: 'hole' }),
    D({ x: 23, y: 59, z: 8.3, r: 6, m: 'hole' }),
    B({ x: 96, y: 34, z: 26, w: 12, d: 12, h: 14, m: 'dark' }),
    B({ x: 99, y: 37, z: 16, w: 6, d: 6, h: 10, m: 'iron' }),
    BEAM({ x: 102, y: 40, z0: 8, z1: 16, r: 2 }),
    D({ x: 102, y: 40, z: 8.4, r: 5, m: 'glow' })
  ],

  /* 소재 페이지 히어로 — 두께 다른 판 4장 */
  materials: () => [
    SH({ x: -10, y: -8, w: 160, d: 100 }),
    B({ x: 0, y: 0, w: 56, d: 40, h: 3, m: 'steel' }),
    B({ x: 66, y: 0, w: 56, d: 40, h: 7, m: 'steel' }),
    B({ x: 0, y: 48, w: 56, d: 40, h: 12, m: 'blue' }),
    B({ x: 66, y: 48, w: 56, d: 40, h: 20, m: 'iron' })
  ],

  /* 문의 페이지 — 우편함 + 서류 */
  contact: () => [
    SH({ x: -10, y: -8, w: 140, d: 100 }),
    B({ x: 0, y: 8, w: 76, d: 64, h: 18, m: 'paper' }),
    B({ x: 4, y: 12, z: 18, w: 68, d: 56, h: 1.5, m: 'dark' }),
    B({ x: 12, y: 18, z: 19.5, w: 52, d: 42, h: 3, m: 'blue' }),
    B({ x: 90, y: 20, z: 0, w: 36, d: 50, h: 2, m: 'paper' }),
    L({ x1: 96, y1: 28, x2: 120, y2: 28, z: 2.3, m: 'ink' }),
    L({ x1: 96, y1: 36, x2: 120, y2: 36, z: 2.3, m: 'ink' }),
    L({ x1: 96, y1: 44, x2: 112, y2: 44, z: 2.3, m: 'ink' })
  ],

  /* 404 — 잘려나간 판 */
  lost: () => [
    SH({ x: -10, y: -8, w: 150, d: 100 }),
    B({ x: 0, y: 0, w: 60, d: 84, h: 5, m: 'steel' }),
    B({ x: 72, y: 0, w: 58, d: 40, h: 5, m: 'steel' }),
    B({ x: 84, y: 52, z: 8, w: 46, d: 32, h: 5, m: 'blue' }),
    L({ x1: 60, y1: 0, x2: 60, y2: 84, z: 5, m: 'kerf' }),
    L({ x1: 72, y1: 40, x2: 130, y2: 40, z: 5, m: 'kerf' })
  ]
};

/* ── 렌더링 ───────────────────────────────────────────────────────── */
const FACE_FILL = {
  steel: ['var(--iso-steel-t)', 'var(--iso-steel-l)', 'var(--iso-steel-r)'],
  iron: ['var(--iso-iron-t)', 'var(--iso-iron-l)', 'var(--iso-iron-r)'],
  blue: ['var(--iso-blue-t)', 'var(--iso-blue-l)', 'var(--iso-blue-r)'],
  dark: ['var(--iso-dark-t)', 'var(--iso-dark-l)', 'var(--iso-dark-r)'],
  paper: ['var(--iso-paper-t)', 'var(--iso-paper-l)', 'var(--iso-paper-r)'],
  spark: ['var(--iso-spark-t)', 'var(--iso-spark-l)', 'var(--iso-spark-r)'],
  screen: ['#5c87ff', '#1b4fe8', '#1039ae']
};

const LINE_STYLE = {
  kerf: { stroke: 'var(--spark)', width: 1.6, glow: true },
  slot: { stroke: 'rgba(12,17,24,.28)', width: 1.4 },
  ink: { stroke: 'var(--iso-iron-l)', width: 1.6 },
  blue: { stroke: 'var(--brand)', width: 1.4 }
};

const DISC_STYLE = {
  hole: { fill: 'var(--iso-steel-t)', stroke: 'rgba(12,17,24,.18)' },
  holeBlue: { fill: 'none', stroke: 'var(--brand)', width: 1.4 },
  glow: { fill: 'var(--spark)', glow: true },
  seal: { fill: 'var(--spark)' },
  sealIn: { fill: 'var(--iso-paper-t)' },
  ring: { fill: 'none', stroke: 'var(--iso-dark-l)', width: 3.2 },
  blueDisc: { fill: 'var(--iso-blue-t)' },
  okDisc: { fill: 'var(--ok)' }
};

/**
 * 화가 알고리즘 정렬.
 *   그림자 → 박스(발자국이 카메라에서 먼 것부터, 같은 자리면 낮은 것부터) → 장식(선·원·빔).
 * 장식은 항상 어떤 박스의 윗면 위에 놓이므로 박스 뒤에 그린다. 발자국 기준으로 섞어 정렬하면
 * 박스 안쪽에 놓인 선이 먼저 그려져 박스에 가려진다.
 */
function sortKey(p) {
  if (p.kind === 'shadow') return -1e9;
  if (p.kind !== 'box') return 1e9 + (p.z ?? p.z0 ?? 0);
  return (p.x + p.w + p.y + p.d) * 10 + (p.z || 0) * 0.5;
}

function bounds(prims) {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  const add = (x, y, z) => {
    const [sx, sy] = project(x, y, z);
    minX = Math.min(minX, sx);
    maxX = Math.max(maxX, sx);
    minY = Math.min(minY, sy);
    maxY = Math.max(maxY, sy);
  };
  prims.forEach(p => {
    if (p.kind === 'box' || p.kind === 'shadow') {
      const z = p.z || 0;
      const h = p.kind === 'box' ? p.h : 0;
      [[p.x, p.y, z], [p.x + p.w, p.y, z], [p.x, p.y + p.d, z], [p.x + p.w, p.y + p.d, z],
        [p.x, p.y, z + h], [p.x + p.w, p.y, z + h], [p.x, p.y + p.d, z + h], [p.x + p.w, p.y + p.d, z + h]].forEach(pt => add(...pt));
    } else if (p.kind === 'line') {
      add(p.x1, p.y1, p.z);
      add(p.x2, p.y2, p.z);
    } else if (p.kind === 'disc') {
      add(p.x - p.r, p.y - p.r, p.z);
      add(p.x + p.r, p.y + p.r, p.z);
    } else if (p.kind === 'beam') {
      add(p.x, p.y, p.z0);
      add(p.x, p.y, p.z1);
    }
  });
  return { minX, minY, maxX, maxY };
}

let uid = 0;

/** 장면을 SVG 로 렌더링한다. scene 은 이름 또는 프리미티브 배열. */
export function IsoIllustration({ scene, className = '', style, pad = 6, title }) {
  const id = useMemo(() => `iso${++uid}`, []);
  const prims = useMemo(() => {
    const list = typeof scene === 'string' ? (SCENES[scene] || SCENES.plate)() : scene;
    return [...list].sort((a, b) => sortKey(a) - sortKey(b));
  }, [scene]);
  const bb = useMemo(() => bounds(prims), [prims]);
  const vb = `${(bb.minX - pad).toFixed(1)} ${(bb.minY - pad).toFixed(1)} ${(bb.maxX - bb.minX + pad * 2).toFixed(1)} ${(bb.maxY - bb.minY + pad * 2).toFixed(1)}`;

  return (
    <svg viewBox={vb} className={`iso-svg ${className}`} style={style} role={title ? 'img' : undefined} aria-hidden={title ? undefined : 'true'}>
      {title && <title>{title}</title>}
      <defs>
        <filter id={`${id}-glow`} x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur stdDeviation="1.6" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <filter id={`${id}-soft`} x="-30%" y="-60%" width="160%" height="220%">
          <feGaussianBlur stdDeviation="2.2" />
        </filter>
        <linearGradient id={`${id}-sheen`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#fff" stopOpacity=".38" />
          <stop offset=".6" stopColor="#fff" stopOpacity="0" />
        </linearGradient>
      </defs>
      {prims.map((p, i) => {
        if (p.kind === 'shadow') {
          const [cx, cy] = project(p.x + p.w / 2, p.y + p.d / 2, p.z || 0);
          return (
            <ellipse key={i} cx={cx} cy={cy} rx={(p.w + p.d) * 0.36} ry={(p.w + p.d) * 0.17} fill="var(--iso-shade)" filter={`url(#${id}-soft)`} />
          );
        }
        if (p.kind === 'box') {
          const f = boxFaces(p);
          const [t, l, r] = FACE_FILL[f.m] || FACE_FILL.steel;
          return (
            <g key={i} opacity={f.op}>
              <polygon points={f.left} fill={l} />
              <polygon points={f.right} fill={r} />
              <polygon points={f.top} fill={t} />
              <polygon points={f.top} fill={`url(#${id}-sheen)`} />
              <polygon points={f.top} fill="none" stroke="rgba(255,255,255,.35)" strokeWidth=".5" />
            </g>
          );
        }
        if (p.kind === 'line') {
          const s = LINE_STYLE[p.m] || LINE_STYLE.ink;
          const [x1, y1] = project(p.x1, p.y1, p.z);
          const [x2, y2] = project(p.x2, p.y2, p.z);
          return (
            <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke={s.stroke} strokeWidth={s.width} strokeLinecap="round" filter={s.glow ? `url(#${id}-glow)` : undefined} />
          );
        }
        if (p.kind === 'disc') {
          const s = DISC_STYLE[p.m] || DISC_STYLE.hole;
          const [cx, cy] = project(p.x, p.y, p.z);
          return (
            <ellipse key={i} cx={cx} cy={cy} rx={p.r * C30 * 1.15} ry={p.r * S30 * 1.15} fill={s.fill} stroke={s.stroke || 'none'} strokeWidth={s.width || 1} filter={s.glow ? `url(#${id}-glow)` : undefined} />
          );
        }
        if (p.kind === 'beam') {
          const [x0, y0] = project(p.x, p.y, p.z0);
          const [x1, y1] = project(p.x, p.y, p.z1);
          return (
            <g key={i} filter={`url(#${id}-glow)`}>
              <line x1={x0} y1={y0} x2={x1} y2={y1} stroke="var(--spark)" strokeWidth={p.r * 2.4} strokeOpacity=".35" strokeLinecap="round" />
              <line x1={x0} y1={y0} x2={x1} y2={y1} stroke="#fff3e6" strokeWidth={p.r} strokeLinecap="round" />
            </g>
          );
        }
        return null;
      })}
    </svg>
  );
}

/** 작은 아이콘 크기 프리셋 */
export function IsoGlyph({ name, size = 56, className = '', style }) {
  return (
    <span className={`iso-glyph ${className}`} style={{ width: size, height: size, display: 'inline-block', ...style }}>
      <IsoIllustration scene={name} pad={4} />
    </span>
  );
}

export const ISO_SCENES = Object.keys(SCENES);
